import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Loader2,
  MailCheck,
} from 'lucide-react';
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import { Button, Card, CardContent, Input } from '@/components/ui';

type SupportedActionMode =
  | 'verifyEmail'
  | 'verifyAndChangeEmail'
  | 'resetPassword'
  | 'recoverEmail';

type ActionState = 'checking' | 'ready' | 'processing' | 'success' | 'error';
const ACTION_CACHE_PREFIX = 'auth-action-processed:';

function isSupportedMode(mode: string | null): mode is SupportedActionMode {
  return (
    mode === 'verifyEmail' ||
    mode === 'verifyAndChangeEmail' ||
    mode === 'resetPassword' ||
    mode === 'recoverEmail'
  );
}

function getActionErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    switch (code) {
      case 'auth/expired-action-code':
        return 'このリンクは有効期限が切れています。再度お手続きをお願いします。';
      case 'auth/invalid-action-code':
        return 'このリンクは無効です。URLをご確認ください。';
      case 'auth/user-disabled':
        return 'このアカウントは無効化されています。';
      case 'auth/user-not-found':
        return 'アカウントが見つかりませんでした。';
      case 'auth/weak-password':
        return '新しいパスワードは8文字以上で入力してください。';
      case 'auth/user-mismatch':
        return 'この操作は別のアカウントで処理されています。ログイン状態をご確認ください。';
      default:
        return '処理に失敗しました。時間をおいて再度お試しください。';
    }
  }

  return '処理に失敗しました。時間をおいて再度お試しください。';
}

export function AuthActionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const fetchUserData = useAuthStore((state) => state.fetchUserData);
  const setUser = useAuthStore((state) => state.setUser);
  const user = useAuthStore((state) => state.user);

  const handledActionRef = useRef<string | null>(null);

  const [state, setState] = useState<ActionState>('checking');
  const [errorMessage, setErrorMessage] = useState('');
  const [resolvedEmail, setResolvedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modeConfig = useMemo(() => {
    switch (mode) {
      case 'verifyEmail':
        return {
          title: 'メール認証',
          description: 'アカウントのメール認証を完了します',
          icon: <MailCheck className="h-5 w-5 text-copper" />,
        };
      case 'verifyAndChangeEmail':
        return {
          title: 'メールアドレス変更の確認',
          description: '新しいメールアドレスへの変更を確定します',
          icon: <MailCheck className="h-5 w-5 text-copper" />,
        };
      case 'resetPassword':
        return {
          title: 'パスワード再設定',
          description: '新しいパスワードを設定してください',
          icon: <KeyRound className="h-5 w-5 text-copper" />,
        };
      case 'recoverEmail':
        return {
          title: 'メールアドレス復旧',
          description: 'メールアドレス復旧リンクを処理します',
          icon: <MailCheck className="h-5 w-5 text-copper" />,
        };
      default:
        return {
          title: '認証操作',
          description: 'メールリンクを処理しています',
          icon: <MailCheck className="h-5 w-5 text-copper" />,
        };
    }
  }, [mode]);

  useEffect(() => {
    const supportedMode = isSupportedMode(mode) ? mode : null;
    const actionKey = `${supportedMode ?? 'unsupported'}:${oobCode ?? 'missing'}`;
    if (handledActionRef.current === actionKey) {
      return;
    }
    handledActionRef.current = actionKey;

    const run = async () => {
      if (!supportedMode) {
        setState('error');
        setErrorMessage('無効なリンクです。メール内のURLをご確認ください。');
        return;
      }

      if (!oobCode) {
        setState('success');
        return;
      }

      const processedCacheKey = `${ACTION_CACHE_PREFIX}${actionKey}`;
      if (
        supportedMode !== 'resetPassword' &&
        typeof window !== 'undefined' &&
        window.sessionStorage.getItem(processedCacheKey) === 'done'
      ) {
        setState('success');
        return;
      }

      try {
        if (supportedMode === 'resetPassword') {
          const email = await verifyPasswordResetCode(auth, oobCode);
          setResolvedEmail(email);
          setState('ready');
          return;
        }

        setState('processing');
        await applyActionCode(auth, oobCode);

        if (auth.currentUser) {
          await auth.currentUser.reload();
          setUser({
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
            emailVerified: auth.currentUser.emailVerified,
            displayName: auth.currentUser.displayName,
            photoURL: auth.currentUser.photoURL,
          });
          await fetchUserData(auth.currentUser.uid);
        }

        if (supportedMode === 'verifyAndChangeEmail') {
          toast.success('メールアドレスの変更が完了しました');
        } else if (supportedMode === 'verifyEmail') {
          toast.success('メール認証が完了しました');
        } else if (supportedMode === 'recoverEmail') {
          toast.success('メールアドレスを復旧しました');
        }

        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(processedCacheKey, 'done');
        }
        setState('success');
      } catch (error) {
        const code =
          error && typeof error === 'object' && 'code' in error
            ? (error as { code: string }).code
            : null;
        if (
          code === 'auth/invalid-action-code' &&
          typeof window !== 'undefined' &&
          window.sessionStorage.getItem(processedCacheKey) === 'done'
        ) {
          setState('success');
          return;
        }
        setState('error');
        setErrorMessage(getActionErrorMessage(error));
      }
    };

    void run();
  }, [fetchUserData, mode, oobCode, setUser]);

  const handleSubmitReset = async () => {
    if (!oobCode) {
      setState('error');
      setErrorMessage('無効なリンクです。再度メールを送信してください。');
      return;
    }

    if (password.length < 8) {
      setErrorMessage('新しいパスワードは8文字以上で入力してください。');
      return;
    }

    if (password !== passwordConfirm) {
      setErrorMessage('パスワード確認が一致しません。');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');
      await confirmPasswordReset(auth, oobCode, password);
      toast.success('パスワードを再設定しました');
      setState('success');
    } catch (error) {
      setErrorMessage(getActionErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (state === 'checking' || state === 'processing') {
      return (
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F6EDE2]">
            <Loader2 className="h-6 w-6 animate-spin text-copper" />
          </div>
          <p className="text-sm text-[#736B5E]">リンクを確認しています...</p>
        </div>
      );
    }

    if (state === 'error') {
      return (
        <div className="space-y-5">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50">
            <AlertTriangle className="h-6 w-6 text-red-500" />
          </div>
          <p className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-700">
            {errorMessage}
          </p>
          <div className="flex flex-col gap-3">
            <Button fullWidth onClick={() => navigate('/login')}>
              ログインへ戻る
            </Button>
            <Link
              to="/forgot-password"
              className="text-center text-sm text-copper hover:underline"
            >
              パスワード再設定メールを再送する
            </Link>
          </div>
        </div>
      );
    }

    if (state === 'ready' && mode === 'resetPassword') {
      return (
        <div className="space-y-5">
          <p className="rounded-2xl border border-[#E8E2D9] bg-[#FCF8F2] px-4 py-3 text-sm leading-relaxed text-[#6F6658]">
            <span className="font-medium text-[#1A1A1A]">{resolvedEmail}</span>
            <br />
            の新しいパスワードを設定します。
          </p>

          <Input
            label="新しいパスワード"
            type="password"
            placeholder="8文字以上で入力"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Input
            label="新しいパスワード（確認）"
            type="password"
            placeholder="確認のため再入力"
            value={passwordConfirm}
            onChange={(event) => setPasswordConfirm(event.target.value)}
          />

          {errorMessage && (
            <p className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorMessage}
            </p>
          )}

          <Button fullWidth isLoading={isSubmitting} onClick={handleSubmitReset}>
            パスワードを更新
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#ECF8F0]">
          <CheckCircle2 className="h-6 w-6 text-[#22A568]" />
        </div>
        <p className="text-sm leading-relaxed text-[#6F6658]">
          {mode === 'verifyAndChangeEmail' &&
            'メールアドレスの変更を確認しました。次回ログイン時から新しいメールアドレスで利用できます。'}
          {mode === 'verifyEmail' &&
            'メール認証が完了しました。引き続きKULUMをご利用ください。'}
          {mode === 'recoverEmail' &&
            'メールアドレスの復旧が完了しました。必要に応じてパスワードを変更してください。'}
          {mode === 'resetPassword' &&
            'パスワードの再設定が完了しました。新しいパスワードでログインしてください。'}
        </p>
        <Button
          fullWidth
          onClick={() => navigate(user?.emailVerified ? '/dashboard' : '/login')}
        >
          {user?.emailVerified ? 'ダッシュボードへ進む' : 'ログインへ進む'}
        </Button>
      </div>
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F6F1E8]">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-copper/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-120px] left-[-80px] h-64 w-64 rounded-full bg-[#D8C3A5]/45 blur-3xl" />
      <div className="pointer-events-none absolute right-[-72px] top-[28%] h-56 w-56 rounded-full bg-[#E7D2B9]/60 blur-3xl" />

      <div className="relative mx-auto flex min-h-screen w-full max-w-xl items-center px-4 py-10">
        <Card className="w-full overflow-hidden border-[#E5D9CA] shadow-[0_24px_70px_rgba(76,53,32,0.12)]">
          <div className="h-1.5 bg-gradient-to-r from-[#C38A50] via-[#B87333] to-[#D6A16C]" />
          <CardContent className="space-y-6 px-6 py-7 sm:px-8">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-[#7B7264] transition hover:text-copper"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>ログインに戻る</span>
            </Link>

            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#E6D8C8] bg-[#FDF9F2] px-3 py-1.5 text-xs font-medium tracking-[0.08em] text-[#8B7C67]">
                {modeConfig.icon}
                <span>{modeConfig.title}</span>
              </div>
              <h1 className="text-2xl font-heading-luxury text-[#1A1A1A]">
                アカウント確認
              </h1>
              <p className="text-sm leading-relaxed text-[#7B7264]">
                {modeConfig.description}
              </p>
            </div>

            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
